import { useEffect, useRef } from 'react';
import GUI, { Controller } from 'lil-gui';
import useSimulationStore from '../store/simulationStore';
import { SimulationControls, ChemicalSpeciesDefinition } from '../types/simulation';

// Hook to populate a lil-gui instance with simulation controls
export const useControlPanelGui = (gui: GUI | null) => {
  // Select state and actions needed
  const controls = useSimulationStore(state => state.controls);
  const { setControls, isRunning, startSimulation, pauseSimulation, resetSimulation, uiFeatures } = useSimulationStore();
  
  const staticControllersRef = useRef<{[key: string]: Controller}>({});
  const startPauseButtonRef = useRef<Controller | null>(null);

  // Update static control keys (remove reactionProbability, decayRate)
  const staticControlKeys: (keyof SimulationControls)[] = [
      'reactorSize', 
      'simulationSpeed', 
      'collisionElasticity', 
      'particleSpeed', 
      'particleScale',
      'tilingFactor',
      'is2DMode'
  ];

  // Effect to setup ONLY static controls and main folders
  useEffect(() => {
    if (!gui || uiFeatures.useNewControls) return; 

    console.log('Setting up GUI Base Structure');
    // Find or create the main folder
    let folder = gui.folders.find(f => f._title === 'Simulation Controls');
    if (!folder) {
        folder = gui.addFolder('Simulation Controls');
    } else {
        // Clear existing static controls if folder exists
        Object.values(staticControllersRef.current).forEach(controller => controller.destroy());
        staticControllersRef.current = {};
        // Also remove start/pause/reset buttons if they exist to avoid duplicates
        folder.controllers.filter(c => ['startPause', 'reset'].includes(c.property)).forEach(c => c.destroy());
        startPauseButtonRef.current = null;
    }
    
    // --- Static Controls Setup --- 
    const guiStateProxy = {
        reactorSize: controls.reactorSize,
        simulationSpeed: controls.simulationSpeed,
        collisionElasticity: controls.collisionElasticity,
        particleSpeed: controls.particleSpeed,
        particleScale: controls.particleScale,
        tilingFactor: controls.tilingFactor,
        is2DMode: controls.is2DMode,
    };

    const addStaticControl = (prop: keyof typeof guiStateProxy, name: string, ...args: any[]) => {
        const controller = folder!.add(guiStateProxy, prop, ...args).name(name);
        controller.onFinishChange(() => {
            const finalValue = (guiStateProxy as any)[prop];
            console.log(`[GUI FinishChange] ${prop}: ${finalValue}`);
            setControls({ [prop]: finalValue } as Partial<SimulationControls>); // Use assertion
        });
        staticControllersRef.current[prop] = controller; 
    };

    // Add static controls based on updated staticControlKeys
    addStaticControl('reactorSize', 'Reactor Size', 10, 150, 10);
    addStaticControl('simulationSpeed', 'Simulation Speed', 0, 1, 0.05); // 0 = paused, 1 = normal speed
    addStaticControl('collisionElasticity', 'Collision Elasticity', 0, 1, 0.05);
    addStaticControl('particleSpeed', 'Particle Speed', 0.5, 10, 0.1);
    addStaticControl('particleScale', 'Particle Scale', 0.1, 10.0, 0.1);
    addStaticControl('tilingFactor', 'Tiling Factor (± Range)', 0, 5, 1);
    
    // Add 2D mode toggle (boolean checkbox)
    const is2DController = folder!.add(guiStateProxy, 'is2DMode').name('2D Mode (XY Plane)');
    is2DController.onChange((value: boolean) => {
        console.log(`[GUI] 2D Mode changed to: ${value}`);
        setControls({ is2DMode: value });
    });
    staticControllersRef.current['is2DMode'] = is2DController;

    // --- Buttons --- 
    const actions = {
      startPause: () => { 
        const currentIsRunning = useSimulationStore.getState().isRunning;
        if (currentIsRunning) pauseSimulation(); else startSimulation();
      },
      reset: resetSimulation,
      fitToViewport: () => {
        const state = useSimulationStore.getState();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        // Use a reasonable default distance - CameraPositioner will recalculate based on reactor size
        const cameraDistance = 50;
        state.fitReactorToViewport(viewportWidth, viewportHeight, cameraDistance, state.cameraFov, state.controls.is2DMode);
      },
    };
    startPauseButtonRef.current = folder!.add(actions, 'startPause').name(isRunning ? 'Pause' : 'Start');
    folder!.add(actions, 'reset').name('Reset');
    folder!.add(actions, 'fitToViewport').name('📐 Fit to Viewport');
    
    // No return cleanup function needed here - we manage controllers within the folder
  
  }, [gui, isRunning, setControls, startSimulation, pauseSimulation, resetSimulation]); // Add dependencies

  // Effect to synchronize static GUI controls when store state changes externally
  useEffect(() => {
    if (!gui || Object.keys(staticControllersRef.current).length === 0) return;
    // console.log('[useControlPanelGui] Syncing static controls with store');
    
    staticControlKeys.forEach(prop => {
        const controller = staticControllersRef.current[prop];
        // Access controls directly, prop is known keyof SimulationControls
        const storeValue = controls[prop as keyof SimulationControls]; 
        
        if (controller) {
            const obj = controller.object as Partial<SimulationControls>; 
            // Only update if property exists and differs
            if (prop in obj && obj[prop] !== storeValue) {
                // console.log(`[useControlPanelGui] Updating GUI for ${prop} from ${obj[prop]} to ${storeValue}`);
                obj[prop] = storeValue as any;
                controller.updateDisplay();
            }
        }
    });

  }, [controls, gui]); // Depend only on controls and gui


  // Effect to update the Start/Pause button name
  useEffect(() => {
    if (startPauseButtonRef.current) {
      startPauseButtonRef.current.name(isRunning ? 'Pause' : 'Start');
    }
  }, [isRunning]);

}; 